import React, { useState, useEffect, useRef } from 'react';
import { GlassCard } from '../../_shared/GlassCard';
import { Eye, UserPlus, Users } from 'lucide-react';

const useCounter = (end, duration = 1400) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const hasAnimated = useRef(false);
    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting && !hasAnimated.current) {
                hasAnimated.current = true;
                const start = performance.now();
                const animate = (now) => {
                    const progress = Math.min(1, (now - start) / duration);
                    const eased = 1 - Math.pow(1 - progress, 3);
                    setCount(Math.floor(end * eased));
                    if (progress < 1) requestAnimationFrame(animate);
                };
                requestAnimationFrame(animate);
            }
        }, { threshold: 0.3 });
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [end, duration]);
    return [count, ref];
};

const KPICard = ({ icon: Icon, iconColor, label, value, subtext }) => {
    const [animatedValue, ref] = useCounter(value);
    return (
        <GlassCard className="flex flex-col justify-between min-h-[180px]">
            <div ref={ref}>
                <div className={`${iconColor} p-3 rounded-xl mb-4 inline-flex`}>
                    <Icon size={22} />
                </div>
                <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-2">{label}</p>
                <p className="text-4xl font-black text-white tracking-tight">
                    {animatedValue.toLocaleString()}
                </p>
                {subtext && <p className="text-white/50 text-sm mt-3">{subtext}</p>}
            </div>
        </GlassCard>
    );
};

export const HeroSection = () => {
    return (
        <section className="min-h-[80vh] flex flex-col justify-center items-center text-center px-4 pt-24 pb-12 relative z-10">
            <div className="space-y-6 max-w-5xl mx-auto w-full">

                <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 backdrop-blur-md mb-2">
                    <span className="text-sm font-medium text-cyan-300 tracking-wide uppercase">Mar – Apr 2026 · 60 Days</span>
                </div>

                <h1 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white via-cyan-100 to-white drop-shadow-lg leading-tight">
                    Source Code Lab<br />60-Day Snapshot
                </h1>

                <p className="text-lg md:text-xl text-cyan-200/70 max-w-2xl mx-auto leading-relaxed">
                    Two months of activity on <span className="text-white font-semibold">@sourcecodelab</span> — and a forward look at how the same content could land much harder with a different presentation.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-12 w-full">
                    <KPICard
                        icon={Eye}
                        iconColor="bg-cyan-500/20 text-cyan-300"
                        label="Total Views (60 Days)"
                        value={28405}
                        subtext="Across all reels &amp; carousels"
                    />
                    <KPICard
                        icon={UserPlus}
                        iconColor="bg-emerald-500/20 text-emerald-300"
                        label="Followers Gained"
                        value={187}
                        subtext="60-day net growth"
                    />
                    <KPICard
                        icon={Users}
                        iconColor="bg-purple-500/20 text-purple-300"
                        label="Current Followers"
                        value={1358}
                        subtext="Started March with 1,171"
                    />
                </div>

                <p className="text-white/40 text-xs mt-6 max-w-xl mx-auto">
                    SCL is in early-stage account building. Per-reel view counts are intentionally not surfaced on this report — the focus is on <span className="text-white/70">presentation strategy</span>, not isolated post performance.
                </p>
            </div>
        </section>
    );
};
